import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { getLocalStorage, setLocalStorage } from "@/lib/utils";

type LocalStorage = {
  sidebarExpanded: boolean;
};

type AppProviderProps = {
  children: React.ReactNode;
  storageKey?: string;
};

type AppProviderState = {
  sidebarExpanded: boolean;
  setSidebarExpanded: (expanded: boolean) => void;
  topbarName: string;
  setTopbarName: (name: string) => void;
  topbarBottomBorder: boolean;
  setTopbarBottomBorder: (divider: boolean) => void;
  topbarCustomActions: React.ReactNode | null;
  setTopbarCustomActions: (actions: React.ReactNode) => void;
};

const initialState: AppProviderState = {
  sidebarExpanded: false,
  setSidebarExpanded: () => null,
  topbarName: "Home",
  setTopbarName: () => null,
  topbarBottomBorder: true,
  setTopbarBottomBorder: () => null,
  topbarCustomActions: undefined,
  setTopbarCustomActions: () => null,
};

const AppProviderContext = createContext<AppProviderState>(initialState);

export const AppProvider: React.FC<AppProviderProps> = ({
  children,
  storageKey = "ui-state",
  ...props
}) => {
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(
    getLocalStorage<LocalStorage>(storageKey)?.sidebarExpanded ?? false
  );
  const [topbarName, setTopbarName] = useState<string>("Home");
  const [topbarBottomBorder, setTopbarBottomBorder] = useState<boolean>(true);
  const [topbarCustomActions, setTopbarCustomActions] =
    useState<React.ReactNode>();

  const setLocalStorageData = useCallback(
    (key: string, value: unknown) => {
      const currentData = getLocalStorage<LocalStorage>(storageKey);
      setLocalStorage(storageKey, { ...currentData, [key]: value });
    },
    [storageKey]
  );

  const value = useMemo(
    () => ({
      sidebarExpanded,
      setSidebarExpanded: (expanded: boolean) => {
        setLocalStorageData("sidebarExpanded", expanded);
        setSidebarExpanded(expanded);
      },
      topbarName,
      setTopbarName,
      topbarBottomBorder,
      setTopbarBottomBorder,
      topbarCustomActions,
      setTopbarCustomActions,
    }),
    [
      sidebarExpanded,
      topbarName,
      topbarBottomBorder,
      topbarCustomActions,
      setLocalStorageData,
    ]
  );

  return (
    <AppProviderContext.Provider {...props} value={value}>
      {children}
    </AppProviderContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppProviderContext);

  if (context === undefined) {
    throw new Error("useApp must be used within a AppProvider");
  }

  return context;
};
